import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import styled from "styled-components";

import { MinusIcon, PlusIcon } from "../../icons/glyphs";
import { isRenderableNode } from "../../utils/reactNode";
import { IconButton, TooltipIconButton } from "../IconButton";
import { Tooltip } from "../Tooltip";
import {
  clampPlaybackSeekTime,
  resolvePlaybackCapabilities,
  resolvePlaybackSeekRange,
  useVideoTimeline,
} from "../Video";
import { PlaybackRateSelect } from "../Video/PlaybackRateSelect";
import { SrOnly } from "../foundations/srOnly";
import { MarkerCluster, NearestMarkerControl, SingleMarker } from "./TimelineMarkers";
import {
  DEFAULT_MEDIA_TIMELINE_SPAN_TONE,
  resolveSelectionDragEdge,
  SELECTION_DRAG_TARGET,
  TimelineSelection,
} from "./TimelineSelection";
import { TimelineSegments } from "./TimelineSegments";
import { SeriesPlot } from "../TimeSeriesChart/SeriesPlot";
import { DEFAULT_SERIES_READOUT_LABELS } from "../TimeSeriesChart/seriesSampling";
import {
  SurfaceTimelineTrackRow,
  MarkerTimelineTrackRow,
  TimelineRuler,
  TimelineTrackStack,
  TimelineTrackRowFrame,
  TimelineTrackRowHeading,
  TimelineTrackRowLanes,
  TimelineTrackRowLaneCaption,
  TimelineTrackRowLaneCaptionText,
  TimelineTrackRowControls,
  TimelineTrackRowTitle,
  TimelineTrackRowActions,
  TimelineTrackHeaderSeparator,
  TimelineTrackRowLabel,
  TimelineTrackRowUnit,
  TimelineNavigationFooter,
} from "./TimelineTrackRows";
import {
  DurationRegion,
  LiveEdgeIndicator,
  SeekableRegion,
  TickCanvas,
  TimelinePlayheadLine,
  TimelinePlayheadCursor,
  TimelineTrackTelemetry,
} from "./TimelineTrack";
import { TimelineViewportNavigation } from "./TimelineViewportNavigation";
import { resolveTimelineTrackLayout, type TimelineNavigationRegion } from "./timelineTrackLayout";
import {
  MediaTransportControls,
  readMediaTimelineCurrent,
  TimelineTimeLabel,
} from "./TimelineTransport";
import {
  clusterMediaTimelinePoints,
  formatMediaTimelineZoom,
  MEDIA_TIMELINE_POLICY,
  mediaTimelinePosition,
  normalizeMediaTimelineSegments,
  normalizeMediaTimelineSelectionRange,
  resolveMediaTimelineSelectionDragSeconds,
  resolveMediaTimelineSelectionEdge,
  resolveMediaTimelineSelectionHandleShifts,
  type MediaTimelineSelectionHandleLayoutInput,
  resolveMediaTimelineEdgePanFraction,
  resolveMediaTimelineOpeningStart,
  resolveMediaTimelineViewportBounds,
  resolveMediaTimelineViewportView,
  snapMediaTimelineTime,
} from "./mediaTimelineMath";
import {
  attachMediaTimelineViewport,
  MediaTimelineViewportController,
  type MediaTimelineViewportSession,
} from "./MediaTimelineViewportController";
import {
  getNormalizedTimeSeries,
  isSeriesDomain,
  type SeriesLane,
} from "../TimeSeriesChart/seriesData";
import { drawTimelineTicks, useTickCanvasStyles } from "./timelineTickCanvas";
import type {
  MediaTimelineBarLabels,
  MediaTimelineBarProps,
  MediaTimelineMarker,
  MediaTimelineMarkersTrack,
  MediaTimelineSegment,
  MediaTimelineSelectionRange,
  MediaTimelineTrack,
  MediaTimelineDataTrack,
  MediaTimelineTrackSelection,
  NormalizedMarker,
  TimelineDragTarget,
  TimelineFocusOrigin,
  TimelineSelectionEdge,
  VisibleMarker,
} from "./timelineTypes";
import { useTrackHeaderResize } from "./useTrackHeaderResize";
import { useTrackToolbar } from "./useTrackToolbar";
import { isPlaybackSpaceBlocked } from "../Video/usePlaybackSpaceShortcut";
import { useTimelineActionFocus } from "./useTimelineActionFocus";
import { useBoundaryFocusRescue } from "./useBoundaryFocusRescue";
import { useMediaTimelineGlobalSpaceShortcut } from "./useMediaTimelineGlobalSpaceShortcut";
import { useMediaTimelineShortcuts } from "./useMediaTimelineShortcuts";
import { useMediaTimelineTrackWarnings } from "./useMediaTimelineTrackWarnings";

export { formatMediaPlaybackTime } from "./mediaTimelineMath";
export type {
  MediaTimelineBarHandle,
  MediaTimelineBarLabels,
  MediaTimelineBarProps,
  MediaTimelineMarker,
  MediaTimelineMarkerBadge,
  MediaTimelineSegment,
  MediaTimelineSegmentTone,
  MediaTimelineTrack,
  MediaTimelineDataTrack,
  MediaTimelineTrackLane,
  MediaTimelineTrackSelection,
  MediaTimelineTrackBase,
  MediaTimelineDefaultTrack,
  MediaTimelineMarkersTrack,
  MediaTimelineSeries,
  MediaTimelineSeriesDomain,
  MediaTimelineSeriesInterpolation,
  MediaTimelineSeriesPoint,
  MediaTimelineTrackVariant,
  MediaTimelineSelectionRange,
  MediaTimelineSelectionTone,
  MediaTimelineViewportBaseline,
  MediaTimelineViewportControllerOptions,
  MediaTimelineViewportSnapshot,
} from "./timelineTypes";
export { MediaTimelineViewportController } from "./MediaTimelineViewportController";

// No surface or padding of its own, like VideoPlaybackBar: the bar is a control cluster, not a
// recessed media frame, so the panel it sits on — and its inset — belongs to the consumer.
const Root = styled.div`
  container: media-timeline / inline-size;
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  gap: 0.45rem;
  /* Programmatic playback fallback when the seek input is disabled; inner controls own navigation rings. */
  &:focus {
    outline: none;
  }
`;

/**
 * Transport/actions and readout/rate/zoom are independent groups. Container queries put the
 * readout group on its own row in narrow hosts, then move zoom below time/rate when needed.
 * Transport actions can wrap within their own group without moving the readout between them.
 */
const TimelineToolbar = styled.div`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.5rem;
  row-gap: 0.35rem;
`;

const ControlCluster = styled.div`
  display: flex;
  min-width: 0;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 0.5rem;
  row-gap: 0.35rem;
`;

const TrailingCluster = styled(ControlCluster)<{ $hasBeforeTimeControl: boolean }>`
  margin-left: auto;
  display: grid;
  grid-template-columns: ${({ $hasBeforeTimeControl }) =>
    $hasBeforeTimeControl ? "auto auto auto auto" : "auto auto auto"};
  grid-template-areas: ${({ $hasBeforeTimeControl }) =>
    $hasBeforeTimeControl ? '"custom time rate zoom"' : '"time rate zoom"'};

  @container media-timeline (max-width: 48rem) {
    width: 100%;
    grid-template-columns: ${({ $hasBeforeTimeControl }) =>
      $hasBeforeTimeControl ? "auto minmax(0, 1fr)" : "1fr auto auto"};
    grid-template-areas: ${({ $hasBeforeTimeControl }) =>
      $hasBeforeTimeControl ? '"custom time" "rate zoom"' : '"time rate zoom"'};
    margin-left: 0;
  }

  @container media-timeline (max-width: 26rem) {
    grid-template-columns: ${({ $hasBeforeTimeControl }) =>
      $hasBeforeTimeControl ? "auto minmax(0, 1fr)" : "minmax(0, 1fr) auto"};
    grid-template-areas: ${({ $hasBeforeTimeControl }) =>
      $hasBeforeTimeControl ? '"custom time" "rate zoom"' : '"time rate" "zoom zoom"'};
  }
`;

const BeforeTimeControl = styled(ControlCluster)`
  grid-area: custom;
  justify-self: start;
  margin-right: 0.25rem;
`;

const TimeReadout = styled.div`
  grid-area: time;
  display: flex;
  align-self: stretch;

  @container media-timeline (max-width: 48rem) {
    > span {
      min-width: 0;
      justify-content: flex-start;
      border-right: 0;
      padding-right: 0;
      margin-right: 0;
    }
  }
`;

/**
 * One trailing affordance cluster. The built-in nearest control and consumer-owned frame
 * actions render as separate sections so each carries its own divider — a shared gap kept
 * reading as one fused group once consumers mounted contextual buttons beside it.
 */
const TrailingControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.15rem;
  margin-left: 0.2rem;
  padding-left: 0.5rem;
  border-left: 1px solid ${({ theme }) => theme.color.border.divider};
`;

const TimelineZoomGroup = styled.div`
  grid-area: zoom;
  justify-self: end;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.1rem;
`;

const TimelineZoomResetButton = styled(IconButton)`
  min-width: 2.5rem;
  font-variant-numeric: tabular-nums;
`;

const PlaybackRateControl = styled.div`
  grid-area: rate;
`;

const TimelineViewport = styled.div<{
  $showRowMetadata: boolean;
  $hasRowControls: boolean;
}>`
  min-width: 0;
  padding: 0 0.1rem;
  --c6-media-track-label-gutter: 8rem;
  padding-inline-start: ${({ $showRowMetadata }) =>
    $showRowMetadata ? "var(--c6-media-track-label-gutter)" : "0.1rem"};

  @container media-timeline (max-width: 26rem) {
    --c6-media-track-label-gutter: ${({ $hasRowControls }) =>
      $hasRowControls ? "8rem" : "5.5rem"};
  }
`;

const Timeline = styled.div`
  position: relative;
  width: 100%;
  min-width: 0;
  user-select: none;
`;

const TIMELINE_NAVIGATION_KEYS = new Set([
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
]);

const TIMELINE_LEFT_GUTTER_PX = MEDIA_TIMELINE_POLICY.leftGutterPx;
const TIMELINE_CANVAS_HEIGHT_PX = MEDIA_TIMELINE_POLICY.canvasHeightPx;
const TIMELINE_DURATION_EPSILON_SECONDS = MEDIA_TIMELINE_POLICY.durationComparisonEpsilonSeconds;
const GLOBAL_PLAYBACK_SHORTCUT_COMMANDS = ["toggle"] as const;

type NormalizedSelection = {
  range: MediaTimelineSelectionRange | null;
  tone?: MediaTimelineTrackSelection["tone"];
  onRangeChange?: MediaTimelineTrackSelection["onRangeChange"];
  segmentId?: string;
};

/** One shared editing engine for standalone spans and selected bands. */
type TimelineSelectionTrack = NormalizedSelection & { id: string; label?: string };

/**
 * Each plot resolves its axis from stable samples; selection changes do not rescan them. Lines
 * always arrive as lanes: a plain `series` row is one unlabeled lane.
 */
type NormalizedDataTimelineTrack = Omit<
  MediaTimelineDataTrack,
  "series" | "lanes" | "selection"
> & {
  lanes?: readonly SeriesLane[];
  selection?: NormalizedSelection;
};

type NormalizedTimelineTrack =
  | Exclude<MediaTimelineTrack, MediaTimelineDataTrack>
  | NormalizedDataTimelineTrack;

/*
 * A series row opens its values popup anywhere in the plot, like the standalone chart, but after
 * a short dwell: the row is also the seek surface and sits in a stack the pointer crosses on its
 * way to the transport, so an instant popup would flash on every pass. Shorter than a tooltip.
 */
const TRACK_READOUT_HOVER_DELAY_MS = 150;

const withTrackUnit = (name: string | undefined, track: NormalizedDataTimelineTrack) =>
  track.unit?.trim() ? `${name ?? track.id} (${track.unit.trim()})` : name;

const seriesTrackLabelOf = (track: NormalizedDataTimelineTrack) =>
  withTrackUnit(track.label, track);

const seriesReadoutLabelOf = (track: NormalizedDataTimelineTrack) =>
  withTrackUnit(track.readoutLabel?.trim() || track.label, track);

const DEFAULT_LABELS: Required<MediaTimelineBarLabels> = {
  ...DEFAULT_SERIES_READOUT_LABELS,
  resizeTrackHeader: "Resize track header",
  play: "Play",
  pause: "Pause",
  stop: "Stop",
  toStart: "Go to start",
  stepBackward: "Step backward",
  stepForward: "Step forward",
  toEnd: "Go to end",
  toMarker: "Go to marker",
  markers: "Markers",
  nearestMarker: "Go to nearest marker",
  seek: "Playback timeline",
  timelineZoom: "Timeline zoom",
  zoomOut: "Zoom timeline out",
  resetZoom: "Reset timeline zoom",
  zoomIn: "Zoom timeline in",
  playbackRate: "Playback speed",
  previousTimelineSegment: "Show previous timeline segment",
  nextTimelineSegment: "Show next timeline segment",
  selectionStart: "Selection start",
  selectionEnd: "Selection end",
  goLive: "Go live",
  live: "Live",
  buffering: "Buffering media",
  waiting: "Waiting for media",
};

// Keyed by the authored `series`/`lanes` arrays, like the series normalization itself: the same
// input yields the same lane objects, so a parent render that only rebuilt `tracks` neither resets
// the readout nor repaints or rescans the canvas.
const laneCache = new WeakMap<object, readonly SeriesLane[]>();
function cachedLanes(key: object, build: () => readonly SeriesLane[]): readonly SeriesLane[] {
  let lanes = laneCache.get(key);
  if (!lanes) {
    lanes = build();
    laneCache.set(key, lanes);
  }
  return lanes;
}

export function MediaTimelineBar({
  ref,
  transport,
  onPrimaryPlaybackToggle,
  stepSeconds = 0.1,
  disabled = false,
  markerTimeSeconds = null,
  onMoveToMarker,
  markers,
  onMarkerSelect,
  onMarkerCreate,
  onMarkerRemove,
  tracks,
  showTrackHeader = true,
  showTrackLabels = false,
  trackHeaderLayout = "inline",
  trackHeaderWidth,
  defaultTrackHeaderWidth,
  minTrackHeaderWidth,
  maxTrackHeaderWidth,
  resizableTrackHeader = false,
  onTrackHeaderWidthChange,
  trackAppearance = "separated",
  viewport,
  segments,
  selectionRange = null,
  onSelectionRangeChange,
  selectionTone = DEFAULT_MEDIA_TIMELINE_SPAN_TONE,
  trailingControl,
  beforeTimeControl,
  labels,
  className,
}: MediaTimelineBarProps) {
  const { arm: armBoundaryFocus, handleGroupActivation } = useBoundaryFocusRescue();
  const rootRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const tickCanvasRef = useRef<HTMLCanvasElement>(null);
  const timelineInputRef = useRef<HTMLInputElement>(null);
  const pointerIdRef = useRef<number | null>(null);
  const pointerCaptureTargetRef = useRef<HTMLElement | null>(null);
  const timelinePointerFocusRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartViewportRef = useRef(0);
  // A handle can sit off its logical edge (spread apart from its sibling, squeezed toward a
  // boundary, even held off-viewport while focused), so a drag moves the handle's *display*
  // with the pointer and inverts the layout for the value — never the pointer's absolute time.
  const selectionDragOriginRef = useRef<{ clientX: number; displayPx: number } | null>(null);
  const selectionDragTrackIdRef = useRef<string | null>(null);
  const selectionDragSegmentIdRef = useRef<string | undefined>(undefined);
  const selectionLayoutRef = useRef<{
    params: Omit<MediaTimelineSelectionHandleLayoutInput, "startPx" | "endPx">;
    startDisplayPx: number;
    endDisplayPx: number;
  } | null>(null);
  // A keyboard edit follows the viewport only once the consumer has applied that exact range —
  // a rejected controlled change must not move the viewport on its own.
  const keyboardFollowRef = useRef<{
    trackId: string;
    segmentId?: string;
    edge: TimelineSelectionEdge;
    range: MediaTimelineSelectionRange;
  } | null>(null);
  const edgePanFrameRef = useRef<number | null>(null);
  const [timelineWidth, setTimelineWidth] = useState<number>(
    MEDIA_TIMELINE_POLICY.initialMeasuredWidthPx,
  );
  const [tickCanvasHeight, setTickCanvasHeight] = useState<number>(TIMELINE_CANVAS_HEIGHT_PX);
  const [rootFontScale, setRootFontScale] = useState(1);
  // The viewport lives in a controller the bar attaches to — the consumer's, or one of its own. The
  // session carries the bar's own moves (zoom around a point, pans, playhead follow), and the
  // snapshot is what gets drawn.
  const [ownViewportController] = useState(() => new MediaTimelineViewportController());
  const viewportController = viewport ?? ownViewportController;
  const viewportSnapshot = useSyncExternalStore(
    viewportController.subscribe,
    viewportController.getSnapshot,
  );
  const viewportSessionRef = useRef<MediaTimelineViewportSession | null>(null);
  const [dragTarget, setDragTarget] = useState<TimelineDragTarget>(null);
  const [edgePanning, setEdgePanning] = useState(false);
  const [timelineFocusVisible, setTimelineFocusVisible] = useState(false);
  const durationSeconds = useVideoTimeline(transport, (snapshot) => snapshot.durationSeconds);
  const seekSupported = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackCapabilities(snapshot).seek,
  );
  const live = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackCapabilities(snapshot).live,
  );
  const seekRangeStart = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackSeekRange(snapshot).startSeconds,
  );
  const seekRangeEnd = useVideoTimeline(
    transport,
    (snapshot) => resolvePlaybackSeekRange(snapshot).endSeconds,
  );
  const text = useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  const hasDuration = Number.isFinite(durationSeconds) && durationSeconds > 0;
  const playbackDisabled = disabled || !hasDuration;
  const seekDisabled = playbackDisabled || !seekSupported;
  const globalPlaybackShortcut = useMediaTimelineShortcuts({
    transport,
    stepSeconds,
    disabled: playbackDisabled,
    commands: GLOBAL_PLAYBACK_SHORTCUT_COMMANDS,
  });
  useMediaTimelineGlobalSpaceShortcut(
    rootRef,
    globalPlaybackShortcut.onKeyDown,
    globalPlaybackShortcut.ariaKeyShortcuts !== "",
  );
  const legacyMarkers = useMemo<readonly MediaTimelineMarker[]>(
    () =>
      markers ??
      (markerTimeSeconds === null
        ? []
        : [{ id: "marker", timeSeconds: markerTimeSeconds, active: true }]),
    [markerTimeSeconds, markers],
  );
  const resolvedTracks = useMemo<readonly MediaTimelineTrack[]>(() => {
    if (tracks !== undefined) return tracks;
    return [
      { id: "default", type: "default" },
      ...(selectionRange
        ? [
            {
              id: "selection",
              type: "track" as const,
              selection: {
                range: selectionRange,
                onRangeChange: onSelectionRangeChange,
                tone: selectionTone,
              },
            },
          ]
        : []),
      ...(segments !== undefined && segments.length > 0
        ? [{ id: "segments", type: "track" as const, segments }]
        : []),
      ...(markers !== undefined || markerTimeSeconds !== null
        ? [
            {
              id: "markers",
              type: "markers" as const,
              variant: "rail" as const,
              markers: legacyMarkers,
            },
          ]
        : []),
    ];
  }, [
    legacyMarkers,
    markerTimeSeconds,
    markers,
    onSelectionRangeChange,
    segments,
    selectionRange,
    selectionTone,
    tracks,
  ]);
  useMediaTimelineTrackWarnings(resolvedTracks, durationSeconds);
  const focusTimeline = useCallback(() => {
    const root = rootRef.current;
    if (!root || isPlaybackSpaceBlocked(root, root)) return;
    timelinePointerFocusRef.current = true;
    setTimelineFocusVisible(false);
    const input = timelineInputRef.current;
    (input && !input.disabled ? input : root).focus({ preventScroll: true });
    timelinePointerFocusRef.current = false;
  }, []);
  const { afterAction: returnAfterPointerAction, afterDismiss: returnAfterPopup } =
    useTimelineActionFocus(rootRef, focusTimeline);
  useImperativeHandle(
    ref,
    () => ({
      focusTimeline,
      returnFocusAfterAction: returnAfterPointerAction,
      returnFocusAfterDismiss: returnAfterPopup,
    }),
    [focusTimeline, returnAfterPointerAction, returnAfterPopup],
  );
  // Whatever a consumer puts in the header slots, a pointer click there hands playback focus back.
  // Keyboard activation keeps its native focus. The tools trigger times its own return around its
  // popup, and the shared return skips focus that moved into a popup or a text entry.
  const onHeadingClick = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (event.detail === 0) return;
      const target = event.target;
      if (!(target instanceof Element) || target.closest("[data-media-timeline-tools-trigger]"))
        return;
      const doc = event.currentTarget.ownerDocument;
      const active = doc.activeElement;
      returnAfterPointerAction(
        active instanceof HTMLElement && active !== doc.body && event.currentTarget.contains(active)
          ? active
          : (target as HTMLElement),
      );
    },
    [returnAfterPointerAction],
  );
  const trackToolbar = useTrackToolbar(
    resolvedTracks,
    showTrackHeader && !disabled,
    timelineInputRef,
    focusTimeline,
    returnAfterPopup,
    returnAfterPointerAction,
  );
  // Range edits replace `tracks`, not the immutable segment sources. Weak keys retain no history.
  const normalizeSegmentSource = useMemo(() => {
    const cache = new WeakMap<readonly MediaTimelineSegment[], readonly MediaTimelineSegment[]>();
    return (source: readonly MediaTimelineSegment[]) => {
      let normalized = cache.get(source);
      if (!normalized) {
        normalized = normalizeMediaTimelineSegments(source, durationSeconds);
        if (source) cache.set(source, normalized);
      }
      return normalized;
    };
  }, [durationSeconds]);
  const normalizedTracks = useMemo<readonly NormalizedTimelineTrack[]>(
    () =>
      resolvedTracks.map<NormalizedTimelineTrack>((track) => {
        if (track.type === "track") {
          const bands = track.segments ? normalizeSegmentSource(track.segments) : undefined;
          const authored = track.selection;
          const segment =
            authored?.segmentId !== undefined
              ? bands?.find((candidate) => candidate.id === authored.segmentId)
              : undefined;
          const selection: NormalizedSelection | undefined = authored
            ? authored.segmentId !== undefined
              ? segment
                ? {
                    range: segment,
                    tone: segment.tone,
                    segmentId: segment.id,
                    onRangeChange: authored.onRangeChange,
                  }
                : undefined
              : {
                  range: normalizeMediaTimelineSelectionRange(authored.range, durationSeconds),
                  tone: authored.tone,
                  onRangeChange: authored.onRangeChange,
                }
            : undefined;
          const { series, lanes: authoredLanes, ...rest } = track;
          // The same authored arrays yield the same lane objects, so a parent render that only
          // rebuilt `tracks` neither resets the readout nor repaints or rescans the canvas.
          const lanes = authoredLanes
            ? cachedLanes(authoredLanes, () =>
                authoredLanes.map<SeriesLane>((lane) => ({
                  id: lane.id,
                  label: lane.label,
                  series: getNormalizedTimeSeries(lane.series),
                  domain: isSeriesDomain(lane.domain) ? lane.domain : undefined,
                })),
              )
            : series
              ? cachedLanes(series, () => [{ id: "", series: getNormalizedTimeSeries(series) }])
              : undefined;
          return { ...rest, lanes, segments: bands, selection };
        }
        return track;
      }),
    [durationSeconds, resolvedTracks, normalizeSegmentSource],
  );
  const selectionTracks = useMemo(
    () =>
      normalizedTracks.flatMap<TimelineSelectionTrack>((track) =>
        track.type === "track" && track.selection
          ? [{ ...track.selection, id: track.id, label: track.label }]
          : [],
      ),
    [normalizedTracks],
  );
  const primaryDefaultTrackId = normalizedTracks.find((track) => track.type === "default")?.id;
  const trackLayout = useMemo(
    () => resolveTimelineTrackLayout(normalizedTracks, trackAppearance),
    [normalizedTracks, trackAppearance],
  );
  const activeRangeTrack =
    selectionTracks.find((track) => track.id === selectionDragTrackIdRef.current) ?? null;
  const activeRangeEditable =
    activeRangeTrack?.range !== null &&
    activeRangeTrack?.onRangeChange !== undefined &&
    activeRangeTrack?.segmentId === selectionDragSegmentIdRef.current &&
    !playbackDisabled;
  const viewportBounds = resolveMediaTimelineViewportBounds(
    durationSeconds,
    viewportController.baseline,
  );
  const { totalRangeSeconds: totalRange, minimumSeconds: minimumViewportDuration } = viewportBounds;
  // Until the bar has synced its media the controller reports no window; draw the opening view
  // that sync is about to publish, so no frame shows anything else.
  const viewportView =
    viewportSnapshot ??
    resolveMediaTimelineViewportView(
      viewportBounds,
      viewportBounds.baselineSeconds,
      resolveMediaTimelineOpeningStart(viewportBounds, readMediaTimelineCurrent(transport)),
    );
  const viewportStart = viewportView.startSeconds;
  const viewportDuration = viewportView.endSeconds - viewportView.startSeconds;
  const visibleEnd = viewportView.endSeconds;
  const timelineCanPanLeft = viewportStart > TIMELINE_DURATION_EPSILON_SECONDS;
  const timelineCanPanRight = visibleEnd < totalRange - TIMELINE_DURATION_EPSILON_SECONDS;
  // Handle corrections are a pixel estimate over the measured width (rem policies rescaled by the
  // root font, like the marker cluster distance); mid-track handles get 0 and sit exactly on the
  // regions' percentage positions.
  const selectionLayouts = useMemo(() => {
    const usableWidth = Math.max(timelineWidth - TIMELINE_LEFT_GUTTER_PX, 1);
    const toPx = (seconds: number) =>
      TIMELINE_LEFT_GUTTER_PX +
      Math.max(0, Math.min(1, (seconds - viewportStart) / viewportDuration)) * usableWidth;
    const halfHit = (MEDIA_TIMELINE_POLICY.selectionHandleHitWidthPx / 2) * rootFontScale;
    const params = {
      widthPx: timelineWidth,
      gutterPx: TIMELINE_LEFT_GUTTER_PX,
      hitWidthPx: MEDIA_TIMELINE_POLICY.selectionHandleHitWidthPx * rootFontScale,
      insetStartPx: halfHit,
      insetEndPx: halfHit,
    };
    return new Map(
      selectionTracks.flatMap((track) => {
        if (!track.range) return [];
        const startPx = toPx(track.range.startSeconds);
        const endPx = toPx(track.range.endSeconds);
        const shifts = resolveMediaTimelineSelectionHandleShifts({ ...params, startPx, endPx });
        return [
          [
            track.id,
            {
              params,
              startDisplayPx: startPx + shifts.startShiftPx,
              endDisplayPx: endPx + shifts.endShiftPx,
              shifts,
            },
          ] as const,
        ];
      }),
    );
  }, [selectionTracks, rootFontScale, timelineWidth, viewportDuration, viewportStart]);
  const resolveSelectionHandleShiftVariables = (trackId: string): Record<string, string> => {
    const layout = selectionLayouts.get(trackId);
    if (!layout) return {};
    const round = (value: number) => Math.round(value * 100) / 100;
    return {
      "--c6-media-selection-handle-shift-start": `${round(layout.shifts.startShiftPx)}px`,
      "--c6-media-selection-handle-shift-end": `${round(layout.shifts.endShiftPx)}px`,
    };
  };
  // Seed/preserve the shared CSS variable on structural renders. Frame-frequency updates are
  // owned by TimelinePlayheadCursor and do not subscribe this control tree to current time.
  const playheadPositionAtRender = mediaTimelinePosition(
    readMediaTimelineCurrent(transport),
    viewportStart,
    viewportDuration,
  );
  const timelineStyle = {
    "--c6-media-playhead-position": playheadPositionAtRender,
    "--c6-media-duration-start": mediaTimelinePosition(0, viewportStart, viewportDuration),
    "--c6-media-duration-end": mediaTimelinePosition(
      hasDuration ? durationSeconds : 0,
      viewportStart,
      viewportDuration,
    ),
    "--c6-media-seekable-start": mediaTimelinePosition(
      seekRangeStart,
      viewportStart,
      viewportDuration,
    ),
    "--c6-media-seekable-end": mediaTimelinePosition(seekRangeEnd, viewportStart, viewportDuration),
  } as CSSProperties;
  const selectionTrackStyle = (
    track: (typeof selectionTracks)[number],
  ): CSSProperties | undefined =>
    track.range
      ? ({
          "--c6-media-selection-start": mediaTimelinePosition(
            track.range.startSeconds,
            viewportStart,
            viewportDuration,
          ),
          "--c6-media-selection-end": mediaTimelinePosition(
            track.range.endSeconds,
            viewportStart,
            viewportDuration,
          ),
          ...resolveSelectionHandleShiftVariables(track.id),
        } as CSSProperties)
      : undefined;
  const showsSeekableRegion =
    live ||
    seekRangeStart > TIMELINE_DURATION_EPSILON_SECONDS ||
    seekRangeEnd < durationSeconds - TIMELINE_DURATION_EPSILON_SECONDS;
  const timelineCanZoom = totalRange > minimumViewportDuration + TIMELINE_DURATION_EPSILON_SECONDS;
  const timelineIsZoomed = !viewportView.atBaseline;
  const timelineCanZoomIn = viewportView.canZoomIn;
  const timelineCanZoomOut = viewportView.canZoomOut;
  const timelineZoomText = formatMediaTimelineZoom(viewportView.zoomFactor);
  const tickCanvasStyles = useTickCanvasStyles();
  const markerTracks = useMemo(
    () => normalizedTracks.filter((track) => track.type === "markers"),
    [normalizedTracks],
  );
  const fallbackMarkerSelectAction =
    tracks !== undefined || markers !== undefined ? onMarkerSelect : onMoveToMarker;
  // Time-sorted once per collection change: the per-frame nearest/at-playhead lookups
  // binary-search this order instead of scanning every marker at telemetry frequency.
  const normalizedMarkerTracks = useMemo<
    readonly { track: MediaTimelineMarkersTrack; points: readonly NormalizedMarker[] }[]
  >(() => {
    if (!hasDuration) return markerTracks.map((track) => ({ track, points: [] }));
    return markerTracks.map((track) => ({
      track,
      points: track.markers
        .flatMap<NormalizedMarker>((marker) =>
          Number.isFinite(marker.timeSeconds)
            ? [
                {
                  trackId: track.id,
                  marker,
                  timeSeconds: Math.max(0, Math.min(durationSeconds, marker.timeSeconds)),
                },
              ]
            : [],
        )
        .sort((left, right) => left.timeSeconds - right.timeSeconds),
    }));
  }, [durationSeconds, hasDuration, markerTracks]);
  const normalizedMarkers = useMemo<readonly NormalizedMarker[]>(
    () =>
      normalizedMarkerTracks
        .flatMap(({ points }) => points)
        .sort((left, right) => left.timeSeconds - right.timeSeconds),
    [normalizedMarkerTracks],
  );
  const markerTrackById = useMemo(
    () => new Map(markerTracks.map((track) => [track.id, track] as const)),
    [markerTracks],
  );
  const selectableMarkers = useMemo(
    () =>
      normalizedMarkers.filter(({ trackId }) => {
        const track = markerTrackById.get(trackId);
        return track?.onMarkerSelect !== undefined || fallbackMarkerSelectAction !== undefined;
      }),
    [fallbackMarkerSelectAction, markerTrackById, normalizedMarkers],
  );
  const canRemoveMarker = useCallback(
    (trackId: string) =>
      markerTrackById.get(trackId)?.onMarkerRemove !== undefined || onMarkerRemove !== undefined,
    [markerTrackById, onMarkerRemove],
  );
  const markerCreateAction =
    markerTracks.find((track) => track.onMarkerCreate !== undefined)?.onMarkerCreate ??
    onMarkerCreate;
  const nearestMarkerActionLabel = text.nearestMarker ?? DEFAULT_LABELS.nearestMarker;
  const selectionLabels = useMemo(
    () => ({
      start: text.selectionStart ?? DEFAULT_LABELS.selectionStart,
      end: text.selectionEnd ?? DEFAULT_LABELS.selectionEnd,
    }),
    [text.selectionEnd, text.selectionStart],
  );
  const selectionLabelsByTrack = useMemo(
    () =>
      new Map(
        selectionTracks.map((track) => {
          if (selectionTracks.length === 1) return [track.id, selectionLabels] as const;
          const prefix = track.label ?? track.id;
          return [
            track.id,
            {
              start: `${prefix}: ${selectionLabels.start}`,
              end: `${prefix}: ${selectionLabels.end}`,
            },
          ] as const;
        }),
      ),
    [selectionTracks, selectionLabels],
  );
  const visibleMarkerGroupsByTrack = useMemo(() => {
    if (!hasDuration) return new Map<string, readonly (readonly VisibleMarker[])[]>();

    const usableWidth = Math.max(timelineWidth - TIMELINE_LEFT_GUTTER_PX, 1);
    return new Map<string, readonly (readonly VisibleMarker[])[]>(
      normalizedMarkerTracks.map(({ track, points }) => {
        const visibleMarkers = points.flatMap<VisibleMarker>(({ trackId, marker, timeSeconds }) => {
          if (timeSeconds < viewportStart || timeSeconds > visibleEnd) return [];
          return [
            {
              trackId,
              marker,
              timeSeconds,
              positionPx:
                TIMELINE_LEFT_GUTTER_PX +
                ((timeSeconds - viewportStart) / viewportDuration) * usableWidth,
            },
          ];
        });
        return [
          track.id,
          clusterMediaTimelinePoints(
            visibleMarkers,
            MEDIA_TIMELINE_POLICY.markerClusterDistancePx * rootFontScale,
          ),
        ] as const;
      }),
    );
  }, [
    hasDuration,
    normalizedMarkerTracks,
    rootFontScale,
    timelineWidth,
    viewportDuration,
    viewportStart,
    visibleEnd,
  ]);

  const selectMarker = useCallback(
    (trackId: string, markerId: string) => {
      const track = markerTrackById.get(trackId);
      if (track?.onMarkerSelect) track.onMarkerSelect(markerId);
      else if (tracks !== undefined || markers !== undefined) onMarkerSelect?.(markerId);
      else onMoveToMarker?.();
    },
    [markerTrackById, markers, onMarkerSelect, onMoveToMarker, tracks],
  );
  const removeMarker = useCallback(
    (markerId: string, trackId: string) => {
      const track = markerTrackById.get(trackId);
      (track?.onMarkerRemove ?? onMarkerRemove)?.(markerId);
    },
    [markerTrackById, onMarkerRemove],
  );

  const seek = useCallback((seconds: number) => transport.seekToSeconds(seconds), [transport]);
  const handleTimelineInputFocus = useCallback(() => {
    if (!timelinePointerFocusRef.current) setTimelineFocusVisible(true);
  }, []);
  const handleTimelineInputBlur = useCallback(() => setTimelineFocusVisible(false), []);
  // A pointer-focused slider hides the ring, and focus events alone never fire again — so
  // keyboard seeking must restore it or arrow navigation runs with no visible indicator.
  // Navigation keys only: Space toggles playback and the capture/delete shortcuts act in
  // place, where forcing the ring re-creates the selection-like track ring pointer seeking
  // deliberately avoids.
  const handleTimelineInputKeyboardHint = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      if (!TIMELINE_NAVIGATION_KEYS.has(event.key)) return;
      timelinePointerFocusRef.current = false;
      setTimelineFocusVisible(true);
    },
    [],
  );
  const focusTimelineAfterSelection = useCallback((origin: TimelineFocusOrigin) => {
    const pointerOrigin = origin === "pointer";
    timelinePointerFocusRef.current = pointerOrigin;
    setTimelineFocusVisible(!pointerOrigin);
    timelineInputRef.current?.focus({ preventScroll: true });
    timelinePointerFocusRef.current = false;
  }, []);
  const resolveViewportNavigationFocusTarget = useCallback(() => {
    if (timelineInputRef.current && !timelineInputRef.current.disabled) {
      return timelineInputRef.current;
    }
    const root = rootRef.current;
    return (
      root?.querySelector<HTMLElement>('[data-media-selection-grip]:not([aria-disabled="true"])') ??
      root?.querySelector<HTMLButtonElement>("button:not(:disabled)") ??
      null
    );
  }, []);

  const zoomTimeline = useCallback((direction: -1 | 1, anchorFraction = 0.5) => {
    viewportSessionRef.current?.zoomAt(direction, anchorFraction);
  }, []);

  const resetTimelineZoom = viewportController.resetZoom;

  const panTimelineEdge = useCallback((direction: -1 | 1, viewportFraction: number) => {
    viewportSessionRef.current?.panByFraction(direction, viewportFraction);
  }, []);

  const stopTimelineEdgePan = useCallback(() => {
    setEdgePanning(false);
    if (edgePanFrameRef.current === null) return;
    window.cancelAnimationFrame(edgePanFrameRef.current);
    edgePanFrameRef.current = null;
  }, []);

  const startTimelineEdgePan = useCallback(
    (direction: -1 | 1) => {
      stopTimelineEdgePan();
      setEdgePanning(true);
      // The immediate nudge keeps a quick tap effective. The held loop then advances by frame
      // timestamps, not frame counts — a fixed per-frame step pans a 144Hz display 2.4x faster
      // than a 60Hz one. Its first frame only establishes the elapsed-time baseline.
      panTimelineEdge(direction, MEDIA_TIMELINE_POLICY.edgePanStepFraction);
      let previousTimestamp: number | null = null;
      const step = (timestamp: number) => {
        if (previousTimestamp !== null) {
          panTimelineEdge(
            direction,
            resolveMediaTimelineEdgePanFraction(timestamp - previousTimestamp),
          );
        }
        previousTimestamp = timestamp;
        edgePanFrameRef.current = window.requestAnimationFrame(step);
      };
      edgePanFrameRef.current = window.requestAnimationFrame(step);
    },
    [panTimelineEdge, stopTimelineEdgePan],
  );

  const hasRowControls = normalizedTracks.some((track) => isRenderableNode(track.controls));
  const hasRowActions = normalizedTracks.some((track) => isRenderableNode(track.actions));
  const hasRowToolbar = normalizedTracks.some((track) => isRenderableNode(track.toolbar));
  const showRowMetadata =
    showTrackHeader && (showTrackLabels || hasRowControls || hasRowActions || hasRowToolbar);
  const headerResize = useTrackHeaderResize({
    visible: showRowMetadata && normalizedTracks.length > 0,
    resizable: resizableTrackHeader,
    width: trackHeaderWidth,
    defaultWidth: defaultTrackHeaderWidth,
    minWidth: minTrackHeaderWidth,
    maxWidth: maxTrackHeaderWidth,
    onWidthChange: onTrackHeaderWidthChange,
  });

  const handleTimelineWheel = useCallback(
    (event: WheelEvent) => {
      if (
        event.target instanceof Element &&
        event.target.closest(
          "[data-media-timeline-row-controls], [data-media-timeline-row-actions], [data-media-timeline-header-resize]",
        )
      ) {
        return;
      }
      if (!event.ctrlKey || playbackDisabled || !timelineCanZoom || event.deltaY === 0) return;
      event.preventDefault();
      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect) return;
      const usableWidth = Math.max(rect.width - TIMELINE_LEFT_GUTTER_PX, 1);
      const anchorFraction = Math.max(
        0,
        Math.min(1, (event.clientX - rect.left - TIMELINE_LEFT_GUTTER_PX) / usableWidth),
      );
      zoomTimeline(event.deltaY < 0 ? 1 : -1, anchorFraction);
    },
    [playbackDisabled, timelineCanZoom, zoomTimeline],
  );

  /** Viewport time under a pointer, unsnapped; each caller applies its own quantization. */
  const resolveTimeFromClientX = useCallback(
    (clientX: number) => {
      const rect = timelineRef.current?.getBoundingClientRect();
      if (!rect) return undefined;
      const usableWidth = Math.max(rect.width - TIMELINE_LEFT_GUTTER_PX, 1);
      const offset = Math.max(
        0,
        Math.min(usableWidth, clientX - rect.left - TIMELINE_LEFT_GUTTER_PX),
      );
      return viewportStart + (offset / usableWidth) * viewportDuration;
    },
    [viewportDuration, viewportStart],
  );

  const resolveSeekFromClientX = useCallback(
    (clientX: number) => {
      const time = resolveTimeFromClientX(clientX);
      if (time === undefined) return;
      const next = snapMediaTimelineTime(time);
      seek(clampPlaybackSeekTime(transport.getSnapshot(), next));
    },
    [resolveTimeFromClientX, seek, transport],
  );

  // Every edit path (drag, Shift+click, handle keys) resolves through the one policy and skips
  // the callback when the policy leaves the range where it is (a handle held at its clamp).
  const commitSelectionEdge = useCallback(
    (trackId: string, edge: TimelineSelectionEdge, candidateSeconds: number, keyboard = false) => {
      const track = selectionTracks.find((candidate) => candidate.id === trackId);
      if (!track?.range || !track.onRangeChange || playbackDisabled) return;
      const next = resolveMediaTimelineSelectionEdge(
        edge,
        candidateSeconds,
        track.range,
        durationSeconds,
        stepSeconds,
      );
      if (
        next.startSeconds === track.range.startSeconds &&
        next.endSeconds === track.range.endSeconds
      ) {
        return;
      }
      keyboardFollowRef.current = keyboard
        ? { trackId, segmentId: track.segmentId, edge, range: next }
        : null;
      track.onRangeChange(next);
    },
    [durationSeconds, playbackDisabled, selectionTracks, stepSeconds],
  );

  // Pointer edits are bounded by the track, but a key can step the focused handle past the
  // viewport. Once the consumer has applied that edit, recenter on the edge (the playhead's own
  // follow rule); the focused handle stays mounted at the boundary in between, so focus holds.
  useLayoutEffect(() => {
    const pending = keyboardFollowRef.current;
    if (!pending) return;
    const rangeTrack = selectionTracks.find((track) => track.id === pending.trackId);
    // An absent/read-only row ends this edit's lifetime; restoring the same id starts a new one.
    if (
      !rangeTrack ||
      rangeTrack.onRangeChange === undefined ||
      rangeTrack.segmentId !== pending.segmentId
    ) {
      keyboardFollowRef.current = null;
      return;
    }
    if (!rangeTrack.range) return;
    if (
      rangeTrack.range.startSeconds !== pending.range.startSeconds ||
      rangeTrack.range.endSeconds !== pending.range.endSeconds
    ) {
      return;
    }
    keyboardFollowRef.current = null;
    viewportSessionRef.current?.follow(
      pending.edge === "start" ? rangeTrack.range.startSeconds : rangeTrack.range.endSeconds,
    );
  }, [selectionTracks]);

  // The playback slider is the natural heir, but a seek-less source disables it while the span
  // stays editable — then the other handle, then any live button in the bar, keeps focus in it.
  const rescueSelectionHandleFocus = useCallback(
    (source: HTMLElement) => {
      const slider = timelineInputRef.current;
      if (slider && !slider.disabled) {
        focusTimelineAfterSelection("keyboard");
        return;
      }
      const root = rootRef.current;
      if (!root) return;
      const isLive = (candidate: HTMLElement) => candidate !== source && candidate.isConnected;
      const fallback =
        Array.from(
          root.querySelectorAll<HTMLElement>(
            '[data-media-selection-grip]:not([aria-disabled="true"])',
          ),
        ).find(isLive) ??
        Array.from(root.querySelectorAll<HTMLElement>("button:not(:disabled)")).find(isLive);
      fallback?.focus({ preventScroll: true });
    },
    [focusTimelineAfterSelection],
  );

  const handleSelectionHandlePointerDown = useCallback(
    (trackId: string, edge: TimelineSelectionEdge, event: ReactPointerEvent<HTMLElement>) => {
      const track = selectionTracks.find((candidate) => candidate.id === trackId);
      if (!track?.range || !track.onRangeChange || playbackDisabled) return;
      const layout = selectionLayouts.get(trackId);
      if (!layout) return;
      selectionLayoutRef.current = layout;
      selectionDragTrackIdRef.current = trackId;
      selectionDragSegmentIdRef.current = track.segmentId;
      selectionDragOriginRef.current = {
        clientX: event.clientX,
        displayPx: edge === "start" ? layout.startDisplayPx : layout.endDisplayPx,
      };
      pointerIdRef.current = event.pointerId;
      event.currentTarget.setPointerCapture(event.pointerId);
      pointerCaptureTargetRef.current = event.currentTarget;
      setDragTarget(SELECTION_DRAG_TARGET[edge]);
    },
    [playbackDisabled, selectionTracks, selectionLayouts],
  );

  const handleTimelinePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>, rangeTrackId?: string) => {
      // Shift+primary places the start, Shift+secondary the end — a modifier click never seeks
      // or moves focus. The track already suppresses the context menu the secondary press
      // would otherwise open.
      const rangeTrack = rangeTrackId
        ? selectionTracks.find((track) => track.id === rangeTrackId)
        : undefined;
      if (
        rangeTrack?.range &&
        rangeTrack.onRangeChange &&
        !playbackDisabled &&
        event.shiftKey &&
        (event.button === 0 || event.button === 2)
      ) {
        event.preventDefault();
        const time = resolveTimeFromClientX(event.clientX);
        if (time !== undefined) {
          commitSelectionEdge(rangeTrack.id, event.button === 2 ? "end" : "start", time);
        }
        return;
      }
      // Shift is reserved for range editing. On rows without an editable range it must remain a
      // no-op instead of falling through to an unrelated seek gesture.
      if (event.shiftKey && (event.button === 0 || event.button === 2)) {
        event.preventDefault();
        return;
      }
      if (event.button !== 0) return;
      // Ctrl+drag pans the viewport — a view gesture like the zoom buttons and edge pan, so it is
      // gated on the bar being usable, not on seek support; a plain press seeks and needs both.
      const pansViewport =
        event.ctrlKey && viewportDuration < totalRange - TIMELINE_DURATION_EPSILON_SECONDS;
      if (pansViewport ? playbackDisabled : seekDisabled) return;
      event.preventDefault();
      timelinePointerFocusRef.current = true;
      setTimelineFocusVisible(false);
      timelineInputRef.current?.focus({ preventScroll: true });
      pointerIdRef.current = event.pointerId;
      event.currentTarget.setPointerCapture(event.pointerId);
      pointerCaptureTargetRef.current = event.currentTarget;

      if (pansViewport) {
        setDragTarget("pan");
        dragStartXRef.current = event.clientX;
        dragStartViewportRef.current = viewportStart;
        return;
      }

      setDragTarget("playhead");
      resolveSeekFromClientX(event.clientX);
    },
    [
      commitSelectionEdge,
      playbackDisabled,
      selectionTracks,
      resolveSeekFromClientX,
      resolveTimeFromClientX,
      seekDisabled,
      totalRange,
      viewportDuration,
      viewportStart,
    ],
  );

  const handleTimelinePointerMove = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerId !== pointerIdRef.current || dragTarget === null) return;

      if (dragTarget === "playhead") {
        resolveSeekFromClientX(event.clientX);
        return;
      }

      const selectionEdge = resolveSelectionDragEdge(dragTarget);
      if (selectionEdge !== null) {
        const origin = selectionDragOriginRef.current;
        const layout = selectionLayoutRef.current;
        const trackId = selectionDragTrackIdRef.current;
        const track = selectionTracks.find((candidate) => candidate.id === trackId);
        if (
          !origin ||
          !layout ||
          !track?.range ||
          track.segmentId !== selectionDragSegmentIdRef.current
        )
          return;
        // The handle follows the pointer's motion on screen; the value is whatever lays it out
        // there (flat stretches keep the current value, so nothing moves without motion).
        commitSelectionEdge(
          track.id,
          selectionEdge,
          resolveMediaTimelineSelectionDragSeconds({
            edge: selectionEdge,
            targetDisplayPx: origin.displayPx + (event.clientX - origin.clientX),
            previousSeconds:
              selectionEdge === "start" ? track.range.startSeconds : track.range.endSeconds,
            otherEdgeSeconds:
              selectionEdge === "start" ? track.range.endSeconds : track.range.startSeconds,
            durationSeconds,
            viewportStart,
            viewportDuration,
            layout: layout.params,
          }),
        );
        return;
      }

      const usableWidth = Math.max(timelineWidth - TIMELINE_LEFT_GUTTER_PX, 1);
      const deltaSeconds =
        (-(event.clientX - dragStartXRef.current) / usableWidth) * viewportDuration;
      viewportSessionRef.current?.panTo(dragStartViewportRef.current + deltaSeconds);
    },
    [
      commitSelectionEdge,
      dragTarget,
      durationSeconds,
      selectionTracks,
      resolveSeekFromClientX,
      timelineWidth,
      viewportDuration,
      viewportStart,
    ],
  );

  const cancelTimelineDrag = useCallback(() => {
    const pointerId = pointerIdRef.current;
    const captureTarget = pointerCaptureTargetRef.current;
    pointerIdRef.current = null;
    pointerCaptureTargetRef.current = null;
    selectionDragOriginRef.current = null;
    selectionDragTrackIdRef.current = null;
    selectionDragSegmentIdRef.current = undefined;
    selectionLayoutRef.current = null;
    timelinePointerFocusRef.current = false;
    setDragTarget(null);
    if (pointerId !== null && captureTarget?.hasPointerCapture(pointerId)) {
      captureTarget.releasePointerCapture(pointerId);
    }
  }, []);

  const finishTimelineDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.pointerId !== pointerIdRef.current) return;
      cancelTimelineDrag();
    },
    [cancelTimelineDrag],
  );

  useEffect(() => {
    const node = timelineRef.current;
    if (!node) return;

    const updateMeasurements = () => {
      const width = node.getBoundingClientRect().width;
      if (width > 0) setTimelineWidth(width);
      // The canvas element is rem-sized, so its rendered height follows the root font size —
      // the bitmap and draw coordinates must follow the element, not the policy constant, or
      // the browser rescales (blurs) the ticks whenever the two disagree.
      const canvasHeight = tickCanvasRef.current?.getBoundingClientRect().height ?? 0;
      if (canvasHeight > 0) setTickCanvasHeight(canvasHeight);
      // Rem-sized marker anchors widen with the root font; px policies authored at the 16px
      // basis (cluster overlap) rescale by this factor. The rem-sized canvas box re-fires the
      // observer on root font changes, so this stays current without its own listener.
      const rootFontSizePx = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
      if (Number.isFinite(rootFontSizePx) && rootFontSizePx > 0) {
        setRootFontScale(rootFontSizePx / MEDIA_TIMELINE_POLICY.rootFontBasisPx);
      }
    };
    updateMeasurements();
    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateMeasurements);
    observer.observe(node);
    const canvas = tickCanvasRef.current;
    if (canvas) observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timeline = timelineRef.current;
    if (!timeline) return;
    timeline.addEventListener("wheel", handleTimelineWheel, { passive: false });
    return () => timeline.removeEventListener("wheel", handleTimelineWheel);
  }, [handleTimelineWheel]);

  useEffect(() => stopTimelineEdgePan, [stopTimelineEdgePan]);

  useEffect(() => {
    if (playbackDisabled || !timelineCanPanLeft || !timelineCanPanRight) {
      stopTimelineEdgePan();
    }
  }, [playbackDisabled, stopTimelineEdgePan, timelineCanPanLeft, timelineCanPanRight]);

  // Attach and sync stay separate: re-attaching resets the view, so a duration change must only
  // sync. Sync opens a fresh view when a transport first gains a usable duration (or is replaced)
  // and otherwise keeps the zoom/pan the user established through duration drift — late metadata,
  // synced channel set changes. Losing the duration re-arms the opening so a reloaded source starts
  // fresh. Layout effects, so a drift correction lands before paint.
  useLayoutEffect(() => {
    const session = attachMediaTimelineViewport(viewportController);
    viewportSessionRef.current = session;
    return () => {
      session.detach();
      if (viewportSessionRef.current === session) viewportSessionRef.current = null;
    };
  }, [viewportController]);

  useLayoutEffect(() => {
    viewportSessionRef.current?.syncMedia({
      source: transport,
      durationSeconds,
      currentTimeSeconds: readMediaTimelineCurrent(transport),
    });
  }, [durationSeconds, transport, viewportController]);

  const followViewportTime = useCallback((timeSeconds: number) => {
    viewportSessionRef.current?.follow(timeSeconds);
  }, []);

  // Each drag ends with the gate that allowed it: a playhead drag when seeking goes away, a
  // viewport pan when the bar disables (or loses its duration); a selection drag is gated on
  // editing alone (below), so it carries on past both.
  useEffect(() => {
    if (dragTarget === "playhead" && seekDisabled) cancelTimelineDrag();
    if (dragTarget === "pan" && playbackDisabled) cancelTimelineDrag();
  }, [cancelTimelineDrag, dragTarget, playbackDisabled, seekDisabled]);

  // A handle drag ends the same way a playhead drag does when the bar disables (or the consumer
  // withdraws editing) mid-gesture: capture, drag state and the pointer-origin latch all release.
  useEffect(() => {
    if (!activeRangeEditable && resolveSelectionDragEdge(dragTarget) !== null) cancelTimelineDrag();
  }, [activeRangeEditable, cancelTimelineDrag, dragTarget]);

  useEffect(() => {
    const canvas = tickCanvasRef.current;
    if (!canvas) return;
    drawTimelineTicks(canvas, {
      widthPx: timelineWidth,
      heightPx: tickCanvasHeight,
      hasDuration,
      durationSeconds,
      viewportStart,
      viewportDuration,
      fontScale: rootFontScale,
      styles: tickCanvasStyles,
    });
  }, [
    durationSeconds,
    hasDuration,
    rootFontScale,
    tickCanvasHeight,
    tickCanvasStyles,
    timelineWidth,
    viewportDuration,
    viewportStart,
  ]);

  const renderNavigation = (region?: TimelineNavigationRegion) => (
    <TimelineViewportNavigation
      key={region?.firstTrackId ?? "footer"}
      region={region}
      canPanLeft={timelineCanPanLeft}
      canPanRight={timelineCanPanRight}
      disabled={playbackDisabled}
      previousLabel={text.previousTimelineSegment}
      nextLabel={text.nextTimelineSegment}
      onPanStart={startTimelineEdgePan}
      onPanStop={stopTimelineEdgePan}
      onPanStep={(direction) =>
        panTimelineEdge(direction, MEDIA_TIMELINE_POLICY.edgePanStepFraction)
      }
      onPointerPan={focusTimeline}
      onKeyboardPan={(source) => armBoundaryFocus(source, resolveViewportNavigationFocusTarget)}
    />
  );

  return (
    <Root
      ref={rootRef}
      className={className}
      aria-keyshortcuts={globalPlaybackShortcut.ariaKeyShortcuts || undefined}
      data-media-timeline-bar=""
      tabIndex={-1}
    >
      <TimelineToolbar data-media-timeline-toolbar="true">
        <ControlCluster data-media-control-cluster="transport">
          <MediaTransportControls
            transport={transport}
            onPrimaryPlaybackToggle={onPrimaryPlaybackToggle}
            onPointerAction={returnAfterPointerAction}
            stepSeconds={stepSeconds}
            disabled={disabled}
            hasDuration={hasDuration}
            seekSupported={seekSupported}
            text={text}
          />
          {selectableMarkers.length > 0 ? (
            <TrailingControl data-media-trailing-section="nearest">
              <NearestMarkerControl
                transport={transport}
                durationSeconds={durationSeconds}
                points={selectableMarkers}
                // Marker selection is a consumer navigation intent, not a transport seek. Live
                // transports can keep their slider locked while a captured-frame marker remains
                // actionable through `onMarkerSelect`.
                disabled={disabled}
                actionLabel={nearestMarkerActionLabel}
                onSelect={(point) => selectMarker(point.trackId, point.marker.id)}
                onFocusTimeline={focusTimelineAfterSelection}
              />
            </TrailingControl>
          ) : null}
          {isRenderableNode(trailingControl) ? (
            <TrailingControl data-media-trailing-section="consumer">
              {trailingControl}
            </TrailingControl>
          ) : null}
        </ControlCluster>
        <TrailingCluster
          $hasBeforeTimeControl={isRenderableNode(beforeTimeControl)}
          data-media-control-cluster="trailing"
        >
          {isRenderableNode(beforeTimeControl) ? (
            <BeforeTimeControl data-media-before-time-control="">
              {beforeTimeControl}
            </BeforeTimeControl>
          ) : null}
          <TimeReadout>
            <TimelineTimeLabel transport={transport} durationSeconds={durationSeconds} />
          </TimeReadout>
          <PlaybackRateControl>
            <PlaybackRateSelect
              transport={transport}
              label={text.playbackRate}
              onReturnFocus={returnAfterPopup}
              disabled={playbackDisabled}
            />
          </PlaybackRateControl>
          {timelineCanZoom ? (
            <TimelineZoomGroup
              role="group"
              aria-label={text.timelineZoom}
              onClick={(event) => {
                handleGroupActivation(event);
                const button = (event.target as Element).closest("button");
                if (event.detail > 0 && button) returnAfterPointerAction(button);
              }}
            >
              <TooltipIconButton
                size="md"
                variant="surface"
                tooltip={{ content: text.zoomOut, placement: "top" }}
                aria-label={text.zoomOut}
                disabled={playbackDisabled || !timelineCanZoomOut}
                onClick={() => zoomTimeline(-1)}
              >
                <MinusIcon />
              </TooltipIconButton>
              <Tooltip content={text.resetZoom} placement="top">
                <TimelineZoomResetButton
                  size="md"
                  variant="ghost"
                  aria-label={`${timelineZoomText}, ${text.resetZoom}`}
                  disabled={playbackDisabled || !timelineIsZoomed}
                  onClick={resetTimelineZoom}
                  text={timelineZoomText}
                />
              </Tooltip>
              <TooltipIconButton
                size="md"
                variant="surface"
                tooltip={{ content: text.zoomIn, placement: "top" }}
                aria-label={text.zoomIn}
                disabled={playbackDisabled || !timelineCanZoomIn}
                onClick={() => zoomTimeline(1)}
              >
                <PlusIcon />
              </TooltipIconButton>
              <SrOnly role="status">{timelineZoomText}</SrOnly>
            </TimelineZoomGroup>
          ) : null}
        </TrailingCluster>
      </TimelineToolbar>
      <TimelineViewport
        ref={headerResize.viewportRef}
        style={headerResize.style}
        $showRowMetadata={showRowMetadata}
        $hasRowControls={hasRowControls || hasRowActions || hasRowToolbar}
        data-media-timeline-viewport="true"
      >
        <Timeline ref={timelineRef} style={timelineStyle}>
          <TimelineRuler
            $disabled={seekDisabled}
            $focusVisible={primaryDefaultTrackId === undefined && timelineFocusVisible}
            data-media-timeline-ruler="true"
            onPointerDown={handleTimelinePointerDown}
            onPointerMove={handleTimelinePointerMove}
            onPointerUp={finishTimelineDrag}
            onPointerCancel={finishTimelineDrag}
            onLostPointerCapture={finishTimelineDrag}
            onContextMenu={(event) => event.preventDefault()}
          >
            <TickCanvas ref={tickCanvasRef} aria-hidden="true" />
            <TimelinePlayheadCursor
              transport={transport}
              timelineRef={timelineRef}
              durationSeconds={durationSeconds}
              viewportStart={viewportStart}
              viewportDuration={viewportDuration}
              dragTarget={dragTarget}
              edgePanning={edgePanning}
              disabled={seekDisabled || normalizedTracks.length === 0}
              onFollowTime={followViewportTime}
              onPointerDown={handleTimelinePointerDown}
              onPointerMove={handleTimelinePointerMove}
              onPointerUp={finishTimelineDrag}
            />
            {normalizedTracks.length > 0 ? (
              <TimelineTrackTelemetry
                transport={transport}
                durationSeconds={durationSeconds}
                stepSeconds={stepSeconds}
                disabled={seekDisabled}
                seekRangeStart={seekRangeStart}
                seekRangeEnd={seekRangeEnd}
                seekLabel={text.seek}
                inputRef={timelineInputRef}
                points={normalizedMarkers}
                canRemoveMarker={canRemoveMarker}
                onFocus={handleTimelineInputFocus}
                onBlur={handleTimelineInputBlur}
                onKeyboardInteraction={handleTimelineInputKeyboardHint}
                onSeek={seek}
                onMarkerCreate={markerCreateAction}
                onMarkerRemove={removeMarker}
              />
            ) : null}
          </TimelineRuler>
          <TimelineTrackStack
            data-media-timeline-track-stack="true"
            data-media-track-appearance={trackAppearance}
          >
            {showRowMetadata && resizableTrackHeader && normalizedTracks.length > 0 ? (
              <TimelineTrackHeaderSeparator
                data-media-timeline-header-resize=""
                role="separator"
                aria-orientation="vertical"
                aria-label={text.resizeTrackHeader}
                tabIndex={0}
                {...headerResize.separatorProps}
                onPointerUp={(event) => {
                  headerResize.separatorProps.onPointerUp(event);
                  // A finished pointer resize hands playback focus back like a button click.
                  returnAfterPointerAction(event.currentTarget);
                }}
              />
            ) : null}
            {normalizedTracks.map((track, index) => {
              const renderRow = () => {
                if (track.type === "default") {
                  const primary = track.id === primaryDefaultTrackId;
                  return (
                    <SurfaceTimelineTrackRow
                      type={track.type}
                      key={track.id}
                      id={track.id}
                      label={track.label}
                      variant={track.variant}
                      disabled={seekDisabled}
                      dragTarget={dragTarget}
                      focusVisible={primary && timelineFocusVisible}
                      onPointerDown={handleTimelinePointerDown}
                      onPointerMove={handleTimelinePointerMove}
                      onPointerUp={finishTimelineDrag}
                      onPointerCancel={finishTimelineDrag}
                      onLostPointerCapture={finishTimelineDrag}
                      onContextMenu={(event) => event.preventDefault()}
                    >
                      <DurationRegion />
                      {(track.showPlayhead ?? true) ? (
                        <TimelinePlayheadLine
                          transport={transport}
                          durationSeconds={durationSeconds}
                          viewportStart={viewportStart}
                          viewportDuration={viewportDuration}
                        />
                      ) : null}
                      {showsSeekableRegion ? (
                        <SeekableRegion data-media-seekable-region="" />
                      ) : null}
                      {live ? (
                        <LiveEdgeIndicator aria-hidden="true" data-media-live-edge="" />
                      ) : null}
                    </SurfaceTimelineTrackRow>
                  );
                }

                if (track.type === "track") {
                  const selection = selectionTracks.find((candidate) => candidate.id === track.id);
                  const editable = !!selection?.range && selection.onRangeChange !== undefined;
                  const ownsSelectionDrag =
                    selectionDragTrackIdRef.current === track.id &&
                    selectionDragSegmentIdRef.current === selection?.segmentId;
                  return (
                    <SurfaceTimelineTrackRow
                      type={track.type}
                      hasSeries={track.lanes !== undefined}
                      laneCount={track.lanes?.length}
                      key={track.id}
                      id={track.id}
                      label={seriesTrackLabelOf(track)}
                      variant={track.variant}
                      disabled={playbackDisabled || (seekDisabled && !editable)}
                      dragTarget={
                        resolveSelectionDragEdge(dragTarget) !== null
                          ? ownsSelectionDrag
                            ? dragTarget
                            : null
                          : dragTarget
                      }
                      style={selection ? selectionTrackStyle(selection) : undefined}
                      onPointerDown={(event) => handleTimelinePointerDown(event, track.id)}
                      onPointerMove={handleTimelinePointerMove}
                      onPointerUp={finishTimelineDrag}
                      onPointerCancel={finishTimelineDrag}
                      onLostPointerCapture={finishTimelineDrag}
                      onContextMenu={(event) => event.preventDefault()}
                    >
                      <DurationRegion />
                      {(track.showPlayhead ?? true) ? (
                        <TimelinePlayheadLine
                          transport={transport}
                          durationSeconds={durationSeconds}
                          viewportStart={viewportStart}
                          viewportDuration={viewportDuration}
                        />
                      ) : null}
                      {track.segments ? (
                        <TimelineSegments
                          segments={track.segments}
                          renderMode={track.renderMode}
                          viewportStart={viewportStart}
                          viewportDuration={viewportDuration}
                        />
                      ) : null}
                      {selection?.range ? (
                        <TimelineSelection
                          key={
                            selection.segmentId === undefined
                              ? "range"
                              : `segment:${selection.segmentId}`
                          }
                          showRegion={selection.segmentId === undefined}
                          range={selection.range}
                          durationSeconds={durationSeconds}
                          viewportStart={viewportStart}
                          viewportDuration={viewportDuration}
                          stepSeconds={stepSeconds}
                          editable={editable}
                          disabled={playbackDisabled}
                          dragTarget={ownsSelectionDrag ? dragTarget : null}
                          tone={selection.tone ?? DEFAULT_MEDIA_TIMELINE_SPAN_TONE}
                          labels={selectionLabelsByTrack.get(track.id) ?? selectionLabels}
                          onHandlePointerDown={(edge, event) =>
                            handleSelectionHandlePointerDown(track.id, edge, event)
                          }
                          onEdgeCandidate={(edge, candidate, keyboard) =>
                            commitSelectionEdge(track.id, edge, candidate, keyboard)
                          }
                          onFocusRescue={rescueSelectionHandleFocus}
                        />
                      ) : null}
                      {track.lanes ? (
                        <SeriesPlot
                          lanes={track.lanes}
                          hoverIntent="plot"
                          hoverDelayMs={TRACK_READOUT_HOVER_DELAY_MS}
                          showAxisRange
                          domain={track.domain}
                          viewportStart={viewportStart}
                          viewportDuration={viewportDuration}
                          bands={track.segments}
                          latestTimeSeconds={durationSeconds}
                          label={seriesReadoutLabelOf(track)}
                          description={track.description}
                          emphasizedSeriesId={track.emphasizedSeriesId}
                          suppressReadout={dragTarget !== null}
                          readoutLabels={text}
                        />
                      ) : null}
                    </SurfaceTimelineTrackRow>
                  );
                }

                const visibleMarkerGroups = visibleMarkerGroupsByTrack.get(track.id) ?? [];
                return (
                  <MarkerTimelineTrackRow
                    key={track.id}
                    id={track.id}
                    label={track.label ?? text.markers}
                    variant={track.variant}
                  >
                    {(track.showPlayhead ?? false) ? (
                      <TimelinePlayheadLine
                        transport={transport}
                        durationSeconds={durationSeconds}
                        viewportStart={viewportStart}
                        viewportDuration={viewportDuration}
                      />
                    ) : null}
                    {visibleMarkerGroups.map((entries) => {
                      const averageTime =
                        entries.reduce((sum, { timeSeconds }) => sum + timeSeconds, 0) /
                        entries.length;
                      const position = mediaTimelinePosition(
                        averageTime,
                        viewportStart,
                        viewportDuration,
                      );
                      const markerIds = entries.map(({ marker }) => marker.id).join("|");
                      const markerDisabled =
                        disabled ||
                        (track.onMarkerSelect === undefined &&
                          fallbackMarkerSelectAction === undefined);

                      return entries.length === 1 && entries[0] ? (
                        <SingleMarker
                          key={entries[0].marker.id}
                          entry={entries[0]}
                          durationSeconds={durationSeconds}
                          position={position}
                          disabled={markerDisabled}
                          fallbackLabel={text.toMarker}
                          onSelect={(markerId) => selectMarker(track.id, markerId)}
                          onFocusTimeline={focusTimelineAfterSelection}
                        />
                      ) : (
                        <MarkerCluster
                          key={`cluster:${markerIds}`}
                          entries={entries}
                          durationSeconds={durationSeconds}
                          position={position}
                          disabled={markerDisabled}
                          fallbackLabel={text.toMarker}
                          groupLabel={text.markers}
                          onSelect={(markerId) => selectMarker(track.id, markerId)}
                          onFocusTimeline={focusTimelineAfterSelection}
                        />
                      );
                    })}
                  </MarkerTimelineTrackRow>
                );
              };
              const trackLabel = showTrackLabels ? (
                <TimelineTrackRowLabel data-media-timeline-row-label={track.id}>
                  {track.label ?? track.id}
                  {track.type === "track" && track.unit?.trim() ? (
                    <TimelineTrackRowUnit data-media-timeline-row-unit="">
                      {` (${track.unit.trim()})`}
                    </TimelineTrackRowUnit>
                  ) : null}
                </TimelineTrackRowLabel>
              ) : null;
              // Captions are names: they follow the row-name switch and need the header gutter.
              const laneCaptions =
                showTrackHeader && showTrackLabels && track.type === "track" && track.lanes
                  ? track.lanes.length > 1
                    ? track.lanes
                    : null
                  : null;
              return (
                <TimelineTrackRowFrame
                  key={track.id}
                  ref={(node) => trackToolbar.registerFrame(track.id, node)}
                  $toolsOpen={trackToolbar.activeId === track.id}
                  data-media-timeline-tools-open={trackToolbar.activeId === track.id || undefined}
                  style={{ gridRow: index + 1 }}
                  $gapBefore={trackLayout.rows[index]?.gapBefore ?? false}
                  $joinBefore={trackLayout.rows[index]?.joinBefore ?? false}
                  $joinAfter={trackLayout.rows[index]?.joinAfter ?? false}
                >
                  {showTrackHeader &&
                  (showTrackLabels ||
                    isRenderableNode(track.controls) ||
                    isRenderableNode(track.actions) ||
                    isRenderableNode(track.toolbar)) ? (
                    <TimelineTrackRowHeading onClick={onHeadingClick}>
                      {isRenderableNode(track.actions) || isRenderableNode(track.toolbar) ? (
                        <TimelineTrackRowTitle $stacked={trackHeaderLayout === "stacked"}>
                          {trackLabel}
                          <TimelineTrackRowActions data-media-timeline-row-actions={track.id}>
                            {trackToolbar.renderTrigger(track)}
                            {track.actions}
                          </TimelineTrackRowActions>
                        </TimelineTrackRowTitle>
                      ) : (
                        trackLabel
                      )}
                      {isRenderableNode(track.controls) ? (
                        <TimelineTrackRowControls data-media-timeline-row-controls={track.id}>
                          {track.controls}
                        </TimelineTrackRowControls>
                      ) : null}
                    </TimelineTrackRowHeading>
                  ) : null}
                  {laneCaptions ? (
                    <TimelineTrackRowLanes data-media-timeline-row-lanes={track.id}>
                      {laneCaptions.map((lane) => (
                        <TimelineTrackRowLaneCaption
                          key={lane.id}
                          data-media-timeline-lane-caption={lane.id}
                        >
                          <TimelineTrackRowLaneCaptionText>
                            {lane.label?.trim() || lane.id}
                          </TimelineTrackRowLaneCaptionText>
                        </TimelineTrackRowLaneCaption>
                      ))}
                    </TimelineTrackRowLanes>
                  ) : null}
                  {renderRow()}
                </TimelineTrackRowFrame>
              );
            })}
            {trackLayout.navigationRegions.map((region) => renderNavigation(region))}
          </TimelineTrackStack>
          {normalizedTracks.length > 0 &&
          trackLayout.navigationRegions.length === 0 &&
          (timelineCanPanLeft || timelineCanPanRight) ? (
            <TimelineNavigationFooter data-media-navigation-footer="true">
              {renderNavigation()}
            </TimelineNavigationFooter>
          ) : null}
        </Timeline>
      </TimelineViewport>
      {trackToolbar.popup}
    </Root>
  );
}
